import { useState } from 'react'
import { ChevronDown, ChevronUp, Columns3, GripVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function ColumnPicker({
  columns,
  visibleIds,
  onToggle,
  onReorder,
  onShowAll,
  onResetDefaults,
  buttonLabel = 'Columns',
}) {
  const [open, setOpen] = useState(false)
  const [dragIndex, setDragIndex] = useState(null)
  const [overIndex, setOverIndex] = useState(null)

  if (!columns.length) return null

  const byId = Object.fromEntries(columns.map((col) => [col.id, col]))
  const visibleCols = visibleIds.map((id) => byId[id]).filter(Boolean)
  const hiddenCols = columns.filter((col) => !visibleIds.includes(col.id))
  const pickerRows = [
    ...visibleCols.map((col) => ({ col, visible: true })),
    ...hiddenCols.map((col) => ({ col, visible: false })),
  ]
  const visibleCount = visibleCols.length

  function handleDragStart(index, visible) {
    if (!visible || !onReorder) return
    setDragIndex(index)
  }

  function handleDragOver(event, index, visible) {
    if (!visible || dragIndex == null || !onReorder) return
    event.preventDefault()
    setOverIndex(index)
  }

  function handleDrop(index, visible) {
    if (!visible || dragIndex == null || !onReorder) {
      setDragIndex(null)
      setOverIndex(null)
      return
    }
    onReorder(dragIndex, index)
    setDragIndex(null)
    setOverIndex(null)
  }

  function handleDragEnd() {
    setDragIndex(null)
    setOverIndex(null)
  }

  function moveVisible(index, direction) {
    if (!onReorder || index < 0 || index >= visibleCount) return
    const target = index + direction
    if (target < 0 || target >= visibleCount) return
    onReorder(index, target)
  }

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((o) => !o)}
      >
        <Columns3 className="h-4 w-4" />
        {buttonLabel} ({visibleIds.length}/{columns.length})
      </Button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-2 w-72 rounded-md border border-border bg-card p-3 shadow-lg">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Show or hide columns
              {onReorder ? ' · use arrows (or drag) to reorder visible columns' : ''}
            </p>
            <ul className="max-h-72 space-y-1 overflow-y-auto text-sm">
              {pickerRows.map(({ col, visible }, index) => {
                const canReorder = Boolean(onReorder && visible)
                const isDragging = canReorder && dragIndex === index
                const isOver = canReorder && overIndex === index && dragIndex !== index
                return (
                  <li
                    key={col.id}
                    draggable={canReorder}
                    onDragStart={() => handleDragStart(index, visible)}
                    onDragOver={(e) => handleDragOver(e, index, visible)}
                    onDrop={() => handleDrop(index, visible)}
                    onDragEnd={handleDragEnd}
                    className={cn(
                      'rounded px-1 py-1',
                      isDragging && 'opacity-50',
                      isOver && 'bg-primary/10',
                      canReorder && 'cursor-grab active:cursor-grabbing',
                    )}
                  >
                    <div className="flex items-center gap-1 hover:bg-muted/50 rounded px-0.5">
                      {onReorder ? (
                        <span
                          className={cn(
                            'shrink-0 text-muted-foreground',
                            !canReorder && 'opacity-30',
                          )}
                          title={canReorder ? 'Drag to reorder' : undefined}
                          aria-hidden
                        >
                          <GripVertical className="h-3.5 w-3.5" />
                        </span>
                      ) : null}
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => onToggle(col.id)}
                        />
                        <span className="min-w-0 flex-1 truncate">{col.label}</span>
                      </label>
                      {canReorder ? (
                        <span className="flex shrink-0 items-center gap-0.5">
                          <button
                            type="button"
                            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                            title="Move up"
                            aria-label={`Move ${col.label} up`}
                            disabled={index === 0}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              moveVisible(index, -1)
                            }}
                          >
                            <ChevronUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                            title="Move down"
                            aria-label={`Move ${col.label} down`}
                            disabled={index >= visibleCount - 1}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              moveVisible(index, 1)
                            }}
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
            <div className="mt-3 flex gap-2 border-t border-border pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={onShowAll}>
                Show all
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={onResetDefaults}>
                Reset
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
